import { Button } from "@babackapp/ui/components/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import { useTranslation } from "react-i18next";
import { tracking } from "@babackapp/transactional-emails/lib";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import useGetAllWorkflowsV2 from "@babackapp/private-api-client/client/use-get-all-workflows-v2";
import { Badge } from "@babackapp/ui/components/badge";
import { SUPPORTED_LOCALES, SupportedLocale, getTrackingStatusTranslation, TrackingStatus } from "@babackapp/tracking-status-registry";


interface StatusItemProps {
  status: TrackingStatus;
  template: (typeof tracking.defaultEmailTemplates)[keyof typeof tracking.defaultEmailTemplates];
  isSelected: boolean;
  onToggle: (statusValue: string) => void;
}

export function toSupportedLocale(locale: string): SupportedLocale {
  const normalizedLocale = locale.split("-")[0]?.toLowerCase() ?? "en";
  if (SUPPORTED_LOCALES.includes(normalizedLocale as SupportedLocale)) {
    return normalizedLocale as SupportedLocale;
  }
  return "en";
}

function StatusItem({
  template,
  isSelected,
  onToggle,
  status,
}: StatusItemProps) {
  const { t, i18n } = useTranslation("trackoffice");
  const locale = toSupportedLocale(i18n.language || "en");
  const linkedWorkflows = useStatusLinkedWorkflows(status);
  const statusTranslations = getTrackingStatusTranslation(status, locale);

  return (
    <div
      className="flex items-start space-x-3 p-3 rounded-md border hover:bg-accent cursor-pointer"
      onClick={() => onToggle(template.type)}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onToggle(template.type)}
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">
          {statusTranslations.title}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {statusTranslations.description}
        </div>
        <div className="text-[8px] text-muted-foreground mt-1 opacity-70">
          {status}
        </div>
      </div>
      {linkedWorkflows.length > 0 && (
        <Badge
          variant="outline"
          className="items-center gap-1 inline-flex"
          size="xs"
        >
          {t(
            "templatesEditor.createDefaultTemplates.linkedWorkflows",
            "Linked workflows",
            {
              count: linkedWorkflows.length,
            },
          )}
        </Badge>
      )}
    </div>
  );
}

export interface StatusSelectionStepProps {
  selectedStatuses: string[];
  onStatusToggle: (statusValue: string) => void;
  onSelectAll: () => void;
  onUnselectAll: () => void;
  onCancel: () => void;
  onNext: () => void;
}

export function StatusSelectionStep({
  selectedStatuses,
  onStatusToggle,
  onSelectAll,
  onUnselectAll,
  onCancel,
  onNext,
}: StatusSelectionStepProps) {
  const { t } = useTranslation("trackoffice");

  const templatesList = Object.entries(tracking.defaultEmailTemplates) as [
    keyof typeof tracking.defaultEmailTemplates,
    (typeof tracking.defaultEmailTemplates)[keyof typeof tracking.defaultEmailTemplates],
  ][];

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {t(
            "templatesEditor.createDefaultTemplates.title",
            "Select statuses for default templates",
          )}
        </DialogTitle>
        <DialogDescription>
          {t(
            "templatesEditor.createDefaultTemplates.description",
            "Choose which tracking statuses you want to create default templates for",
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="flex gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={onSelectAll}>
          {t("templatesEditor.createDefaultTemplates.selectAll", "Select all")}
        </Button>
        <Button variant="outline" size="sm" onClick={onUnselectAll}>
          {t(
            "templatesEditor.createDefaultTemplates.unselectAll",
            "Unselect all",
          )}
        </Button>
      </div>

      <div className="overflow-y-auto pr-2 max-h-[50vh]">
        <div className="space-y-2">
          {templatesList.map(([key, template]) => (
            <StatusItem
              key={String(key)}
              status={key as TrackingStatus}
              template={template}
              isSelected={selectedStatuses.includes(template.type)}
              onToggle={onStatusToggle}
            />
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t("templatesEditor.createDefaultTemplates.cancel", "Cancel")}
        </Button>
        <Button onClick={onNext} disabled={selectedStatuses.length === 0}>
          {t("templatesEditor.createDefaultTemplates.next", "Next")}
        </Button>
      </DialogFooter>
    </>
  );
}

export default function useStatusLinkedWorkflows(status: TrackingStatus) {
  const { data: shop } = useCurrentShopSuspense();
  const { data: workflows } = useGetAllWorkflowsV2(shop.id);

  return (workflows || []).filter((workflow) => {
    return (
      workflow.trigger.data?.triggerType === "tracking_status_change" &&
      "trackingStatus" in workflow.trigger.data.config &&
      workflow.trigger.data.config.trackingStatus === status
    );
  });
}
